import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import he from 'he';

import { ErrorTextComp } from '../../../../inputs/fields.component';
import GoalServices from '../../../../../services/goal.services';
import { reactLoader, removeReactLoader } from '../../../../../utils/common';
import DeleteCommonModal from '../../../../deleteCommonModal.component';
import { TEMP_ID_PREFIX } from './cascadeKrCreateModal';
import { ValidationPopup } from '../validationPopup.component';
import { CascadePrimaryKrSection } from './cascadePrimaryKrSection';
import { CascadeKrListSection } from './cascadeKrListSection';
import { CascadeAdditionalSection, AdditionalSectionChanges } from './cascadeKrAdditionalSection';
import {
  CascadeKRCompProps,
  CascadeKRCompRef,
  MetricObj,
  ValidationErrors,
  CascadeKrItem,
  User,
} from './cascadeKrTypes';
import { validateCascadeKrForm } from './validation';
import { isKrDurationChangedFromSaved } from '../../../../../utils/krQuarterDuration.js';

// jQuery declarations
declare const $: {
  (selector: string): {
    val: () => string;
    length: number;
    css: (prop: string, value: string | number) => void;
    parent: () => {
      animate: (props: Record<string, number>) => void;
    };
    attr: (prop: string, value: string) => void;
    focus: () => void;
    scrollHeight: number;
  };
};

const DEFAULT_PRIMARY_KR_TYPE = 2; // cascade KR
const DEFAULT_CASCADE_KR_TYPE = 1; // simple KR

const CascadeKRComp = forwardRef<CascadeKRCompRef, CascadeKRCompProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    krText,
    krNote,
    item,
    goalDetails,
    addOkrData,
    actions,
    realOwner,
    coOwners: propsCoOwners,
    teamId,
    closeKrRightPane,
    setSavedAnnualDuration,
    kr_duration: krDuration,
    isQuarter,
    krType,
    lastType,
    addEditFlag,
    onlyMetEdit,
    clusterData,
    MetricChangeWeight,
    nplTags: propsNplTags,
    selectednlplTags: propsSelectedNlplTags,
    selectednlplTagsName: propsSelectedNlplTagsName,
    tagsList: propsTagsList,
    groupKRCustomFields,
    onShowDismissButtonChange,
    customFieldsData,
    customData,
    onCustomFieldsChange,
  } = props;

  // Refs for services and mutable data
  const goalServicesRef = useRef(new GoalServices());

  // Initialize metric object
  const getInitialMetricObj = useCallback((): MetricObj => {
    let metricObj: MetricObj = {
      name: krText,
      interval: 2,
      weekly_update_day: 5,
      target: '',
      stretch: '',
      lower_limit: '',
      upper_limit: '',
      counting: 1,
      progress_consider: false,
      initial_data: 0,
      metric: 1,
      metricCascdeUserList: [],
      progress_type: 1,
      measure_metric_data: item.measure_metric_data ? item.measure_metric_data : 5,
    };

    const isStretchEnabled = addOkrData.isStretchEnabled ?? goalDetails.isStretchEnabled ?? false;
    const committedKRFlag =
      isStretchEnabled && item && parseInt(item.committed_target || '0') ? parseInt(item.committed_target || '0') : 0;

    if (Object.keys(item).length) {
      metricObj = { ...metricObj, ...item };
      metricObj.progress_consider = metricObj.progress_consider === ('0' as unknown as boolean);

      if (metricObj.progress_type === 1 || metricObj.progress_type === 2) {
        metricObj.progress_type = 2;
      }

      if (metricObj.metric === ('0' as unknown as number)) {
        metricObj.metric = 1;
      }

      if (item.type === 1) {
        metricObj.message = '';
        metricObj.progress_type = 2;
      }

      metricObj.counting = metricObj.counting || 1;
      metricObj.initial_data = metricObj.initial_data === '' ? 0 : metricObj.initial_data;

      if (isStretchEnabled && item.stretch_target !== null) {
        metricObj.stretch = item.stretch_target || '';
      } else if (isStretchEnabled && item.target && !committedKRFlag) {
        metricObj.stretch = item.target;
      } else {
        metricObj.stretch = '';
      }

      const shouldSetTarget =
        (!isStretchEnabled && item.target) ||
        (isStretchEnabled && committedKRFlag && item.stretch_target === null) ||
        (isStretchEnabled && item.stretch_target !== null);
      metricObj.target = shouldSetTarget ? item.target : '';
    }

    return metricObj;
  }, [krText, item, addOkrData, goalDetails]);

  // Initialize cascade list
  const getInitialCascadeList = useCallback(
    (): CascadeKrItem[] =>
      item?.metricCascdeUserList?.map((cascadeItem: CascadeKrItem) => ({
        id: cascadeItem.id,
        name: cascadeItem.name,
        initial_data: cascadeItem.initial_data ?? 0,
        target: cascadeItem.target,
        counting: cascadeItem.counting,
        metric: cascadeItem.metric,
        owner: {
          id: cascadeItem.owner_id || 'TBD',
          name: `${cascadeItem.firstname} ${cascadeItem.lastname}`,
          avatar: cascadeItem.avatar || 'TBD',
        },
        lower_limit: cascadeItem.lower_limit,
        upper_limit: cascadeItem.upper_limit,
        cascade_children_count: cascadeItem.cascade_children_count,
        progress: cascadeItem.progress ?? 0,
      })) || [],
    [item],
  );

  // State hooks - organized by domain
  const [metricObj, setMetricObj] = useState<MetricObj>(getInitialMetricObj);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Permission states (for saving) - managed by CascadeAdditionalSection
  const [teamPermissionSelected, setTeamPermissionSelected] = useState<unknown[]>([]);
  const [bizPermissionSelected, setBizPermissionSelected] = useState<unknown[]>([]);
  const [permissionState, setPermissionState] = useState<number[]>([]);

  // Cascade KR states
  const [cascadeList, setCascadeList] = useState<CascadeKrItem[]>(getInitialCascadeList);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  // Align & Dependencies states
  const [krAlignSelected, setKrAlignSelected] = useState<unknown[]>(
    item && item.kr_aligns !== undefined && item.kr_aligns.length > 0 ? item.kr_aligns : [],
  );
  const [krDependenciesSelected, setKrDependenciesSelected] = useState<unknown[]>(
    item && item.kr_dependent !== undefined && item.kr_dependent.length > 0 ?
      item.kr_dependent :
      [],
  );

  // Tags states (for saving) - managed by CascadeAdditionalSection
  const [tags, setTags] = useState<string[]>([]);
  const [selectedNlplTagsName, setSelectedNlplTagsName] = useState<string[]>([]);

  // Milestones state (for saving)
  const [milestones, setMilestones] = useState<
    Array<{ id?: number; date: number; label?: string; target: number }>
  >(item?.milestones ?? []);

  // Custom fields states (for saving) - managed by CascadeAdditionalSection

  // UI states
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [isAnnualQChange, setIsAnnualQChange] = useState(false);

  // Other states
  const isStretchEnabled = addOkrData.isStretchEnabled ?? goalDetails.isStretchEnabled ?? false;

  // Effects
  useEffect(() => {
    setSavedAnnualDuration(item.isQuarter, item.quarter_name, item.id);
  }, [item.isQuarter, item.quarter_name, item.id, setSavedAnnualDuration]); // componentDidMount

  useEffect(() => {
    setMetricObj((prev) => ({
      ...prev,
      name: krText,
    }));
  }, [krText]); // componentDidUpdate for krText changes

  // Callbacks and handlers
  const renderError = useCallback(
    (field: string) => <ErrorTextComp key={field} errors={errors} field={field} />,
    [errors],
  );

  const handlePrimaryKrChange = useCallback(
    (updates: Partial<MetricObj>, clearedErrors?: string[]) => {
      setMetricObj((prev) => ({ ...prev, ...updates }));
      if (clearedErrors && clearedErrors.length > 0) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          clearedErrors.forEach((errorField) => {
            delete newErrors[errorField];
          });
          return newErrors;
        });
      }
    },
    [setMetricObj, setErrors],
  );

  const handleCascadeListChange = useCallback(
    (newList: CascadeKrItem[], clearedErrors?: string[]) => {
      setCascadeList(newList);
      if (clearedErrors && clearedErrors.length > 0) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          clearedErrors.forEach((errorField) => {
            delete newErrors[errorField];
          });
          return newErrors;
        });
      }
    },
    [setCascadeList, setErrors],
  );

  const handleValidationMessageChange = useCallback((message: string | null) => {
    setValidationMessage((prevMessage) => (message === prevMessage ? null : message));
  }, []);

  const handleAdditionalSectionChange = useCallback((changes: AdditionalSectionChanges) => {
    // Update metricObj if changed
    if (changes.metricObj) {
      setMetricObj((prev) => ({ ...prev, ...changes.metricObj }));
    }
    // Update states needed for saving
    if (changes.customData !== undefined) {
      onCustomFieldsChange?.(changes.customData);
    }
    if (changes.tags !== undefined) {
      setTags(changes.tags);
    }
    if (changes.selectednlplTagsName !== undefined) {
      setSelectedNlplTagsName(changes.selectednlplTagsName);
    }
    if (changes.krAlignSelected !== undefined) {
      setKrAlignSelected(changes.krAlignSelected);
    }
    if (changes.krDependenciesSelected !== undefined) {
      setKrDependenciesSelected(changes.krDependenciesSelected);
    }
    if (changes.teamPermissionSelected !== undefined) {
      setTeamPermissionSelected(changes.teamPermissionSelected);
    }
    if (changes.bizPermissionSelected !== undefined) {
      setBizPermissionSelected(changes.bizPermissionSelected);
    }
    if (changes.permissionstate !== undefined) {
      setPermissionState(changes.permissionstate);
    }
    if (changes.milestones !== undefined) {
      setMilestones(changes.milestones);
    }
  }, [onCustomFieldsChange]);

  const exitDeleteModal = useCallback(() => {
    setConfirmPopup(false);
  }, []);

  const saveItemsCompleted = useCallback(
    (callback: (data?: Record<string, unknown>) => void, data: Record<string, unknown> = {}) => {
      window.dispatchEvent(new CustomEvent('krDataRefresh'));
      if (callback && typeof callback === 'function') {
        callback(data);
      } else {
        closeKrRightPane(true);
      }
    },
    [closeKrRightPane],
  );

  const saveItems = useCallback(
    (callback: ((data?: Record<string, unknown>) => void) | null = null) => {
      const params: Record<string, unknown> = {};
      const {
        counting,
        initial_data: initialData,
        target,
        lower_limit: lowerLimit,
        upper_limit: upperLimit,
        stretch,
      } = metricObj;
      const goalId = goalDetails.id;
      const permission = goalDetails.permission || '';

      if (typeof goalId !== 'undefined') {
        let krId: string | undefined;
        if (typeof item !== 'undefined' && typeof item.id !== 'undefined') {
          params.id = item.id;
          krId = params.id as string;
        }

        const nlpIds = selectedNlplTagsName && selectedNlplTagsName.length ? selectedNlplTagsName : [''];
        const tagids = tags.length ? tags : [''];
        const finalTagsIds = [...nlpIds, ...tagids];

        params.krTagIds = finalTagsIds;
        params.name = he.encode(krText);
        params.kr_duration = krDuration;
        params.isQuarter = isQuarter;
        params.isPeriodChange = isAnnualQChange;
        params.description = krNote;
        params.interval = metricObj.interval;
        params.weekly_update_day = metricObj.weekly_update_day;
        if (metricObj.measure_metric_data === 2) {
          params.progress_type = 3;
        } else if (metricObj.measure_metric_data === 1) {
          params.progress_type = 2;
        } else {
          params.progress_type = 1;
        }
        params.counting = counting;
        params.metric = metricObj.metric;
        params.source = 9;
        params.real_owner = realOwner.id;

        const coOwnersToSave = propsCoOwners && propsCoOwners.length > 0 ? propsCoOwners : [];
        params.coOwners =
          coOwnersToSave.length > 0 ? Array.prototype.map.call(coOwnersToSave, (s: User) => s.id).toString() : '';

        params.type = DEFAULT_PRIMARY_KR_TYPE;
        params.initial_data = initialData;
        params.lower_limit = lowerLimit;
        params.upper_limit = upperLimit;
        params.target = target;
        params.message = metricObj.message ? metricObj.message : '';
        params.progress_consider = metricObj.progress_consider ? 0 : 1;
        params.customField = JSON.stringify(customFieldsData);

        const createdCascadeList = cascadeList.filter(
          (cascadeItem) => cascadeItem.id.startsWith(TEMP_ID_PREFIX)
        );

        const formattedCascadeList = createdCascadeList.map((cascadeItem) => {
          const ownerId = cascadeItem.owner?.id || cascadeItem.owner_id;

          return {
            id: undefined,
            owner_id: ownerId,
            name: cascadeItem.name,
            target: cascadeItem.target,
            initial_data: cascadeItem.initial_data ?? metricObj.initial_data,
            interval: cascadeItem.interval ?? metricObj.interval,
            weekly_update_day: cascadeItem.weekly_update_day ?? metricObj.weekly_update_day,
            counting: cascadeItem.counting ?? metricObj.counting,
            lower_limit: cascadeItem.lower_limit ?? '',
            upper_limit: cascadeItem.upper_limit ?? '',
            measure_metric_data: cascadeItem.measure_metric_data ?? metricObj.measure_metric_data,
            metric: cascadeItem.metric ?? metricObj.metric,
            progress_type: cascadeItem.progress_type ?? metricObj.progress_type,
            message: cascadeItem.message ?? metricObj.message ?? '',
            owner: cascadeItem.owner,
            type: DEFAULT_CASCADE_KR_TYPE,
          };
        });

        params.cascadeListJson = JSON.stringify(formattedCascadeList);

        params.stretchTarget = stretch;
        if (counting !== 4) {
          params.upper_limit = '';
          params.lower_limit = '';
        } else {
          params.target = '';
          params.stretchTarget = '';
        }

        if (goalDetails && goalDetails.all_metrics === 1) {
          params.permission_teams = goalDetails.permission_teams;
          params.permission_biz = goalDetails.permission_biz;
          params.permission = permission;
        } else {
          params.permission_teams = teamPermissionSelected.map((val) => (val as { id: string }).id);
          params.permission_biz = bizPermissionSelected.map((val) => (val as { id: string }).id);
          params.permission = permissionState.join(',');
        }
        params.measure_metric_data = metricObj.measure_metric_data;

        const krAlignSel = krAlignSelected;
        const dependentKrsSel = krDependenciesSelected;
        const krAligns: string[] = [];
        const dependentKrs: string[] = [];

        if (Object.keys(krAlignSel).length > 0) {
          krAlignSel.forEach((alignItem: { id: string }) => {
            krAligns.push(alignItem.id);
          });
        }

        if (Object.keys(dependentKrsSel).length > 0) {
          dependentKrsSel.forEach((depItem: { id: string }) => {
            dependentKrs.push(depItem.id);
          });
        }

        params.krAligns = krAligns;
        params.dependentKr = dependentKrs;

        // Match getMilestonesPayload from person/rollup/linkedkr/cascade.component.js exactly
        const milestonesPayload =
          Array.isArray(milestones) && milestones.length > 0
            ? milestones
                .filter((milestone) => milestone.date && (milestone.target == 0 || milestone.target))
                .reduce(
                  (acc, milestone, index) => ({
                    ...acc,
                    [index]: {
                      id: milestone.id && typeof milestone.id === 'number' ? milestone.id : null,
                      date: milestone.date,
                      label: milestone.label,
                      target: milestone.target,
                    },
                  }),
                  {} as Record<string, unknown>
                )
            : '';
        params.milestones =
          typeof milestonesPayload === 'object' && Object.keys(milestonesPayload).length > 0
            ? milestonesPayload
            : '';

        reactLoader(true);
        goalServicesRef.current
          .addEditKr({ goalId, metric: params })
          .then((response: { data: { data: Record<string, unknown> } }) => {
            removeReactLoader();
            if (response.data) {
              setConfirmPopup(false);
              setIsAnnualQChange(false);

              if (!onlyMetEdit) {
                let result: Record<string, unknown> = response.data.data;
                let krWeightage = null;
                if (result.krWeightage) {
                  krWeightage = result.krWeightage;
                  delete result.krWeightage;
                }
                result = {
                  clusterItems: clusterData,
                  clusterCount: clusterData?.length || 0,
                  ...result,
                };
                if (krId && actions) {
                  actions.editNewKr(krId, result);
                } else if (actions) {
                  actions.addNewKr(result);
                }
                if (krWeightage && MetricChangeWeight) {
                  MetricChangeWeight(krWeightage);
                }
              }
              saveItemsCompleted(callback, response.data.data);
            }
          });
      }
    },
    [
      metricObj,
      goalDetails,
      item,
      selectedNlplTagsName,
      tags,
      krText,
      krDuration,
      isQuarter,
      isAnnualQChange,
      krNote,
      realOwner,
      propsCoOwners,
      customFieldsData,
      cascadeList,
      teamPermissionSelected,
      bizPermissionSelected,
      permissionState,
      krAlignSelected,
      krDependenciesSelected,
      milestones,
      onlyMetEdit,
      clusterData,
      actions,
      MetricChangeWeight,
      saveItemsCompleted,
    ],
  );

  const saveCascadeResults = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_isError?: boolean) => {
      const {
        initial_data: initialData,
        counting,
        target,
        lower_limit: lowerLimit,
        upper_limit: upperLimit,
        stretch,
      } = metricObj;
      const metricNameElement = $('#metric-name-text');
      const metricName = metricNameElement.val() as string | undefined;

      // Perform all validations
      const validationErrors = validateCascadeKrForm(
        metricName,
        initialData.toString(),
        target,
        stretch,
        lowerLimit,
        upperLimit,
        counting,
        isStretchEnabled,
        cascadeList.length,
        t,
      );

      if (Object.keys(validationErrors).length) {
        setErrors(validationErrors);
        // Scroll to error
        setTimeout(() => {
          const krRightPaneElement = $('.krRightPaneContent');
          if (validationErrors.metricName) {
            krRightPaneElement.parent().animate({ scrollTop: 0 });
            metricNameElement.attr('aria-invalid', 'true');
            metricNameElement.focus();
          } else {
            let isErrorFocus = false;
            const errorKeys = Object.keys(validationErrors);
            for (let idx = 0; idx < errorKeys.length; idx += 1) {
              const key = errorKeys[idx];
              const ele = document.getElementById(key);
              if (ele) {
                isErrorFocus = true;
                krRightPaneElement.parent().animate({ scrollTop: ele.clientHeight });
                break;
              }
            }
            if (!isErrorFocus) {
              krRightPaneElement.parent().animate(
                { scrollTop: krRightPaneElement.scrollHeight - 150 }
              );
            }
          }
        }, 0);
      } else if (krType !== lastType && addEditFlag) {
        setConfirmPopup(true);
      } else if (isKrDurationChangedFromSaved(item, krDuration, goalDetails)) {
        setConfirmPopup(true);
        setIsAnnualQChange(true);
      } else {
        saveItems(null);
      }
    },
    [
      metricObj,
      cascadeList,
      isStretchEnabled,
      krType,
      lastType,
      addEditFlag,
      item,
      krDuration,
      goalDetails,
      t,
      saveItems,
    ],
  );

  // Expose saveCascadeResults to parent component via ref
  useImperativeHandle(ref, () => ({
    saveCascadeResults,
  }));

  // Render
  const isError = Object.keys(errors).length > 0;
  const isNew = typeof metricObj.id === 'undefined';
  const isOldDis = !isNew && typeof metricObj.type !== 'undefined' && metricObj.type === 2;

  // Compute initial permission values
  const getInitialTeamPermissions = useCallback(() => {
    if (item && item.permission_teams) {
      return item.permission_teams;
    }
    if (
      goalDetails.type === 1 &&
      goalDetails.default_permission_teams &&
      Object.keys(goalDetails.default_permission_teams).length > 0
    ) {
      return goalDetails.default_permission_teams;
    }
    return [];
  }, [item, goalDetails]);

  const getInitialPermissionState = useCallback(() => {
    if (item && item.permission) {
      return item.permission.split(',').map(Number);
    }
    if (goalDetails.type === 1) {
      const hasDefaultPermission =
        goalDetails.default_permission && Object.keys(goalDetails.default_permission).length > 0;
      if (hasDefaultPermission) {
        return goalDetails.default_permission.split(',').map(Number);
      }
      return [8];
    }
    return [10];
  }, [item, goalDetails]);

  const initialTeamPermissions = useMemo(
    () => getInitialTeamPermissions(),
    [getInitialTeamPermissions],
  );
  const initialBizPermissions = useMemo(
    () => (item && item.permission_biz ? item.permission_biz : []),
    [item],
  );
  const initialPermissionState = useMemo(
    () => getInitialPermissionState(),
    [getInitialPermissionState],
  );

  return (
    <div className="cascade-kr-comp-wrapper">
      <CascadePrimaryKrSection
        metricObj={metricObj}
        isError={isError}
        isStretchEnabled={isStretchEnabled}
        isOldDis={isOldDis}
        addOkrData={addOkrData}
        renderError={renderError}
        onChange={handlePrimaryKrChange}
        cascadeList={cascadeList}
      />
      <div className="krBreak" aria-hidden="true" style={{ marginTop: '15px' }} />
      <CascadeKrListSection
        cascadeList={cascadeList}
        metricObj={metricObj}
        isOldDis={isOldDis}
        addOkrData={addOkrData}
        realOwner={realOwner}
        teamId={teamId}
        goalId={goalDetails.id}
        isError={isError}
        renderError={renderError}
        onCascadeListChange={handleCascadeListChange}
        onValidationMessageChange={handleValidationMessageChange}
        onShowDismissButtonChange={onShowDismissButtonChange}
      />
      {cascadeList.length > 0 && (
        <CascadeAdditionalSection
          metricObj={metricObj}
          item={item}
          goalDetails={goalDetails}
          addOkrData={addOkrData}
          krText={krText}
          customData={customData}
          groupKRCustomFields={groupKRCustomFields}
          renderError={renderError}
          isCustomSave={[]}
          props={props}
          nplTags={propsNplTags || []}
          selectedNlplTags={propsSelectedNlplTags || []}
          selectedNlplTagsName={propsSelectedNlplTagsName || []}
          tagsList={propsTagsList || []}
          teamPermissionSelected={initialTeamPermissions}
          bizPermissionSelected={initialBizPermissions}
          permissionState={initialPermissionState}
          milestones={milestones}
          onChange={handleAdditionalSectionChange}
          saveKr={saveItems}
        />
      )}

      {confirmPopup && (
        <DeleteCommonModal
          exitDeleteModal={exitDeleteModal}
          deleteItems={saveItems}
          id=""
          title={t(isAnnualQChange ? 'Save Key Result with new duration' : 'Save Key Result with New Data Type')}
          deleteContentOne=""
          deleteContentSecondParam=""
          deletefirstTitle=""
          deleteContentSecond={t(
            isAnnualQChange
              ? 'Are you sure you want to change the duration on this Key Result? This will eliminate all Updates and Confidence Flags on the Key Result.'
              : 'Are you sure you want to save this key result with the new data type you selected? This will get rid of past key result data.',
          )}
          buttonText={t(isAnnualQChange ? 'Change duration' : 'Save')}
        />
      )}
      <div className="breakline" />

      {validationMessage && (
        <ValidationPopup
          key={validationMessage}
          message={validationMessage}
          onClose={() => setValidationMessage(null)}
        />
      )}
    </div>
  );
});

CascadeKRComp.displayName = 'CascadeKRComp';

export default CascadeKRComp;
